import {
  mergeClasses,
  Toast,
  Toaster,
  ToastTitle,
  useToastController,
} from "@fluentui/react-components";
import {
  CheckmarkCircle24Regular,
  Dismiss20Regular,
  ErrorCircle24Regular,
  Info24Regular,
  Warning24Regular,
} from "@fluentui/react-icons";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { dequeueToast } from "../../redux/toastSlice";
import {
  TOAST_OFFSET,
  TOAST_POSITION,
  TOAST_TIMEOUT,
  TOASTER_ID,
} from "./toast.constants";
import { useToastStyles } from "./toast.styles";

const intentIcons: Record<string, JSX.Element> = {
  warning: <Warning24Regular aria-hidden="true" />,
  error: <ErrorCircle24Regular aria-hidden="true" />,
  info: <Info24Regular aria-hidden="true" />,
  success: <CheckmarkCircle24Regular aria-hidden="true" />,
};

export const ToastHost = (): JSX.Element => {
  const styles = useToastStyles();
  const { dispatchToast, dismissToast } = useToastController(TOASTER_ID);
  const queue = useSelector((s: RootState) => s.toastSlice.queue);
  const dispatch = useDispatch();

  const intentStyles: Record<string, string> = {
    warning: styles.warning,
    error: styles.error,
    info: styles.info,
    success: styles.success,
  };

  useEffect(() => {
    queue.forEach((t) => {
      const toastId = crypto.randomUUID();

      const isCritical = t.intent === "error" || t.intent === "warning";

      const content = (
        <Toast
          className={mergeClasses(styles.toast, intentStyles[t.intent])}
          {...(isCritical && { role: "alert", "aria-live": "assertive" })}
        >
          <ToastTitle
            media={intentIcons[t.intent]}
            action={
              <button
                aria-label="Dismiss notification"
                className={styles.dismissButton}
                onClick={() => dismissToast(toastId)}
              >
                <Dismiss20Regular />
              </button>
            }
          >
            {t.message}
          </ToastTitle>
        </Toast>
      );

      dispatchToast(content, {
        intent: t.intent,
        toastId,
        timeout: TOAST_TIMEOUT,
      });

      dispatch(dequeueToast(t.id));
    });
  }, [queue, dispatch]);

  return (
    <Toaster
      toasterId={TOASTER_ID}
      position={TOAST_POSITION}
      offset={TOAST_OFFSET}
      timeout={TOAST_TIMEOUT}
      className={styles.toaster}
    />
  );
};
